import { Box, CssBaseline } from "@mui/material";
import { useContext, useState } from "react";
import { Outlet } from "react-router-dom";

import { IncomingCallsDrawer } from "../../components/calls/incomingCalls";
import Sidebar from "../../components/sidebar/sidebar";
import Unauthorized from "../../components/Unauthorized";
import AWSCognitoContext from "../../contexts/aws-cognito-context";
import { useFullscreen } from "../../contexts/fullscreen-context";
import { stylesMode } from "../../themes/styles";

import { LayoutSection } from "./layout-section";
import { Main } from "./main";

import type { Breakpoint } from "@mui/material/styles";

const MinimalLayout = () => {
  const layoutQuery: Breakpoint = "md"; // Medium breakpoint for responsive behavior
  const [sidebarWidth, setSidebarWidth] = useState(100); // Default sidebar width
  const user = useContext(AWSCognitoContext)?.user;
  const { isFullscreen } = useFullscreen();

  if (user?.role !== "Agent") return <Unauthorized />;

  return (
    <LayoutSection
      sx={{
        display: "flex",
        "&::before": {
          width: 1,
          height: 1,
          zIndex: -1,
          content: "''",
          opacity: 0.24,
          position: "fixed",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          backgroundImage: `url(/assets/background/overlay.jpg)`,
          [stylesMode.dark]: { opacity: 0.08 },
        },
      }}
    >
      <CssBaseline />

      {/* Global incoming call drawer — always mounted so it auto-opens on any route */}
      <IncomingCallsDrawer />

      {/* Sidebar Component - Placed Outside Main to Avoid Layout Issues */}
      {!isFullscreen && <Sidebar onWidthChange={setSidebarWidth} />}

      {/* Main Content */}
      <Main
        layoutQuery={layoutQuery}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1, // Takes the full remaining space
          marginLeft: isFullscreen ? 0 : `${sidebarWidth}px`, // Shifts the content area based on sidebar width
          // transition: "margin-left 0.3s ease-in-out", // Smooth transition
          minHeight: "100vh",
          overflow: "hidden",
          width: isFullscreen ? "100vw" : `calc(100vw - ${sidebarWidth}px)`,
        }}
      >
        {/* Content Area */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            width: isFullscreen ? "100vw" : `calc(100vw - ${sidebarWidth}px)`,
            overflow: "hidden",
          }}
        >
          <Outlet />
        </Box>
      </Main>
    </LayoutSection>
  );
};

export default MinimalLayout;
